import moment from 'moment';
import React, { useEffect, useRef } from 'react';

import { FormSuccessMessage } from '@common/components/AuthLayout/Layout';
import { BasketItem } from '@common/components/BasketItem';
import { PricePerCover } from '@common/components/PricePerCover/PricePerCover';
import { useTranslations } from '@lang';
import BASKET_UPDATE_TYPES from '@common/constants/BasketUpdateTypes';
import { some } from '@utils/some';
import { BasketVoucher } from 'shared-types/Basket';
import { Table as TableType } from 'shared-types/Common';
import { TableDetails } from './TableDetails';
import { TableMetadata } from './TableMetadata';

export type TableProps = {
  onChange: (
    type: BASKET_UPDATE_TYPES.QUANTITY | BASKET_UPDATE_TYPES.REMOVE,
    itemId: number | string,
    quantity?: number
  ) => void;
  item: TableType;
  isEditable: boolean;
  thumbnail: string;
  tablesDisableChildBookings: boolean;
  hidePrice?: boolean;
  hideGuestDetails?: boolean;
  basketVouchers?: Array<BasketVoucher>;
};

export const Table: React.FC<TableProps> = ({
  onChange,
  item,
  thumbnail,
  isEditable,
  tablesDisableChildBookings,
  hidePrice,
  hideGuestDetails,
  basketVouchers,
}) => {
  const { t } = useTranslations();
  const {
    bookable: {
      imageUrl,
      ids: { restaurant, restaurantName },
      name,
      price: tableTotalPrice,
      pricesPerNight,
    },
    id,
    discount,
    children,
    adults,
    checkIn,
    price,
  } = item;
  const dateNameFormat = 'dddd D MMMM - HH:mm';
  const dateName = useRef(moment(checkIn).format(dateNameFormat));
  const dietaryRequirements = some(item.amendments)
    ? item.amendments?.find((amendment) => amendment.type === 'dietary_requirements')?.amendments
    : item.dietaryRequirements || [];
  const celebrations = some(item.amendments)
    ? item.amendments?.find((amendment) => amendment.type === 'celebrations')?.amendments
    : item.celebrations || [];

  useEffect(() => {
    dateName.current = moment(checkIn).format(dateNameFormat);
  }, [checkIn]);

  function renderMetadata() {
    return (
      <TableMetadata
        numberOfChildren={children}
        numberOfAdults={adults}
        restaurantName={restaurantName}
        restaurant={restaurant}
        tablesDisableChildBookings={tablesDisableChildBookings}
        item={item}
      />
    );
  }

  function handleTableRemoval() {
    onChange(BASKET_UPDATE_TYPES.REMOVE, id);
  }

  const shouldShowVoucherSuccessMessage = (): boolean => {
    if (!basketVouchers?.length) return false;

    const tableVouchers = basketVouchers.filter((voucher) => {
      if ('basketItem' in voucher && voucher.basketItem) {
        return voucher.basketItem === id;
      }
      return false;
    });
    return tableVouchers.length > 0;
  };

  const renderVoucherSuccessMessage = (): React.ReactElement | null => {
    if (!shouldShowVoucherSuccessMessage()) return null;
    return <FormSuccessMessage>{t('basket.voucherTableSuccess')}</FormSuccessMessage>;
  };

  const shouldRenderMobileDetails = Boolean(name);

  return (
    <>
      <BasketItem
        key={id}
        name={dateName.current}
        discount={discount}
        thumbnail={imageUrl || thumbnail}
        onRemove={handleTableRemoval}
        price={price?.amount ? price : null}
        renderMetadata={renderMetadata}
        isEditable={isEditable}
        renderDetails={
          !hideGuestDetails ? (
            <TableDetails
              name={name}
              dietaryRequirements={dietaryRequirements}
              celebrations={celebrations}
            />
          ) : undefined
        }
        shouldRenderMobileDetails={shouldRenderMobileDetails}
        pricePerNight={
          tableTotalPrice?.amount && tableTotalPrice.amount !== pricesPerNight?.[0] ? (
            <PricePerCover
              totalPrice={tableTotalPrice}
              numGuests={(adults ?? 0) + (children ?? 0)}
            />
          ) : (
            <></>
          )
        }
        hidePrice={hidePrice}
      />
      {renderVoucherSuccessMessage()}
    </>
  );
};
